import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../../services/api';
import { useAuth } from '../../Auth';

const projectCache = new Map();
const ownerCache = new Map();
const sprintCache = new Map();

const UNKNOWN_PROJECT_LABEL = 'Projet sans nom';

const projectRequestsInFlight = new Set();
const ownerRequestsInFlight = new Set();
const sprintRequestsInFlight = new Set(); // keyed by projectId

const safeNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getEmployeeDisplayName = (employee) => {
  if (!employee) {
    return null;
  }
  return (
    employee.name ||
    employee.full_name ||
    [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim() ||
    employee.email ||
    null
  );
};

export const useObjectiveMetadata = (objectives = []) => {
  const { user } = useAuth();
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchMissingMetadata = async () => {
      const promises = [];

      const projectIdsToFetch = [
        ...new Set(
          objectives
            .filter(
              (obj) =>
                !obj?.project_name &&
                safeNumber(obj?.project_id) !== null &&
                !projectCache.has(safeNumber(obj.project_id))
            )
            .map((obj) => safeNumber(obj.project_id))
        ),
      ].filter((id) => id !== null && !projectRequestsInFlight.has(id));

      projectIdsToFetch.forEach((id) => projectRequestsInFlight.add(id));

      if (projectIdsToFetch.length) {
        promises.push(
          Promise.all(
            projectIdsToFetch.map(async (id) => {
              try {
                const project = await apiService.get(`/projects/${id}`);
                projectCache.set(
                  id,
                  project?.name ||
                    project?.project_number ||
                    project?.project_code ||
                    UNKNOWN_PROJECT_LABEL
                );
              } catch (error) {
                console.warn(`Impossible de récupérer le projet ${id}:`, error);
              } finally {
                projectRequestsInFlight.delete(id);
              }
            })
          )
        );
      }

      const ownerIdsToFetch = [
        ...new Set(
          objectives
            .filter(
              (obj) =>
                !obj?.owner_name &&
                safeNumber(obj?.owner_id) !== null &&
                !ownerCache.has(safeNumber(obj.owner_id))
            )
            .map((obj) => safeNumber(obj.owner_id))
        ),
      ].filter((id) => id !== null && !ownerRequestsInFlight.has(id));

      ownerIdsToFetch.forEach((id) => ownerRequestsInFlight.add(id));

      if (ownerIdsToFetch.length) {
        promises.push(
          Promise.all(
            ownerIdsToFetch.map(async (id) => {
              try {
                const employee = await apiService.get(`/employees/${id}`);
                const label =
                  getEmployeeDisplayName(employee) || `Employé #${id}`;
                ownerCache.set(id, label);
              } catch (error) {
                console.warn(`Impossible de récupérer l'employé ${id}:`, error);
              } finally {
                ownerRequestsInFlight.delete(id);
              }
            })
          )
        );
      }

      const sprintPairs = objectives
        .filter(
          (obj) =>
            !obj?.sprint_name &&
            safeNumber(obj?.sprint_id) !== null &&
            safeNumber(obj?.project_id) !== null &&
            !sprintCache.has(
              `${safeNumber(obj.project_id)}:${safeNumber(obj.sprint_id)}`
            )
        )
        .map((obj) => ({
          projectId: safeNumber(obj.project_id),
          sprintId: safeNumber(obj.sprint_id),
        }));

      const sprintIdsByProject = new Map();
      sprintPairs.forEach(({ projectId, sprintId }) => {
        if (!sprintIdsByProject.has(projectId)) {
          sprintIdsByProject.set(projectId, new Set());
        }
        sprintIdsByProject.get(projectId).add(sprintId);
      });

      sprintIdsByProject.forEach((sprintIds, projectId) => {
        if (sprintRequestsInFlight.has(projectId)) {
          return;
        }
        sprintRequestsInFlight.add(projectId);
        promises.push(
          (async () => {
            try {
              const sprints = await apiService.get(`/projects/${projectId}/sprints`);
              sprints.forEach((sprint) => {
                const label =
                  sprint?.name ||
                  sprint?.description ||
                  `Sprint #${sprint?.id}`;
                sprintCache.set(`${projectId}:${sprint?.id}`, label);
              });
              sprintIds.forEach((sprintId) => {
                const key = `${projectId}:${sprintId}`;
                if (!sprintCache.has(key)) {
                  sprintCache.set(key, `Sprint #${sprintId}`);
                }
              });
            } catch (error) {
              console.warn(
                `Impossible de récupérer les sprints du projet ${projectId}:`,
                error
              );
              sprintIds.forEach((sprintId) => {
                const key = `${projectId}:${sprintId}`;
                if (!sprintCache.has(key)) {
                  sprintCache.set(key, `Sprint #${sprintId}`);
                }
              });
            } finally {
              sprintRequestsInFlight.delete(projectId);
            }
          })()
        );
      });

      if (!promises.length) {
        return;
      }

      try {
        await Promise.all(promises);
      } finally {
        if (!cancelled) {
          setRefreshIndex((value) => value + 1);
        }
      }
    };

    fetchMissingMetadata();

    return () => {
      cancelled = true;
    };
  }, [objectives]);

  const currentUserId = useMemo(() => {
    return (
      safeNumber(user?.employee_id) ??
      safeNumber(user?.id) ??
      safeNumber(user?.user_id) ??
      null
    );
  }, [user]);

  const currentUserName =
    user?.name || user?.full_name || user?.email || 'Moi';

  const getProjectName = useCallback((objective) => {
    if (!objective) {
      return 'Projet non précisé';
    }
    if (objective.project_name) {
      return objective.project_name;
    }
    if (objective.project?.name) {
      return objective.project.name;
    }
    const projectId =
      safeNumber(objective.project_id) ?? safeNumber(objective.project?.id);
    if (projectId === null) {
      return 'Projet non précisé';
    }
    return projectCache.get(projectId) ?? UNKNOWN_PROJECT_LABEL;
  }, [refreshIndex]);

  const getOwnerName = useCallback(
    (objective) => {
      if (!objective) {
        return 'Non assigné';
      }
      if (objective.owner_name) {
        return objective.owner_name;
      }
      if (objective.owner?.name) {
        return objective.owner.name;
      }
      const ownerId =
        safeNumber(objective.owner_id) ?? safeNumber(objective.owner?.id);
      if (ownerId !== null) {
        if (currentUserId !== null && ownerId === currentUserId) {
          return currentUserName;
        }
        return ownerCache.get(ownerId) ?? 'Non assigné';
      }
      if (currentUserId !== null) {
        return currentUserName;
      }
      return 'Non assigné';
    },
    [currentUserId, currentUserName, refreshIndex]
  );

  const getSprintName = useCallback(
    (objective) => {
      if (!objective) {
        return 'Non précisé';
      }
      if (objective.sprint_name) {
        return objective.sprint_name;
      }
      if (objective.sprint?.name || objective.sprint?.description) {
        return objective.sprint?.name || objective.sprint?.description;
      }
      const sprintId =
        safeNumber(objective.sprint_id) ?? safeNumber(objective.sprint?.id);
      const projectId =
        safeNumber(objective.project_id) ?? safeNumber(objective.project?.id);
      if (sprintId === null) {
        return 'Non précisé';
      }
      if (projectId === null) {
        return `Sprint #${sprintId}`;
      }
      const cacheKey = `${projectId}:${sprintId}`;
      return sprintCache.get(cacheKey) ?? `Sprint #${sprintId}`;
    },
    [refreshIndex]
  );

  return { getProjectName, getOwnerName, getSprintName };
};
